import React, { useState, useMemo, useCallback } from 'react';
import { Flex, Box, Text, useColorModeValue } from '@chakra-ui/react';
import useBank from './services/useBank';
import usePartner from './services/usePartner';
import Header from './Header';
import Toolbar from './Toolbar';
import BanksChart from './BanksChart';
import ProvidersChart from './ProvidersChart';
import PartnersChart from './PartnersChart';
import PartnersTable from './PartnersTable';
import './index.scss';

export default function Statistics() {
  const textColor = useColorModeValue('gray.700', 'white');
  const [startedFrom, setStartDate] = useState(initStartDate());
  const [finishedTo, setEndDate] = useState(initEndDate());
  const [isOnlyActivePartnersShowed, setIsOnlyActivePartnersShowed] =
    useState(true);
  const [env, onSelectEnv] = useState('all');
  const { bankList } = useBank(env);
  const { partnerList } = usePartner(env);

  const daysList = useMemo(() => {
    const result = [];

    if (!startedFrom) return result;

    const startedTime = startedFrom.getTime();
    const finishedTime = finishedTo
      ? finishedTo.getTime()
      : new Date().setHours(23, 59, 59, 999);

    for (let i = startedTime; i < finishedTime; i = i + 1000 * 60 * 60 * 24) {
      result.push(getTimestamp(new Date(i)));
    }

    return result;
  }, [startedFrom, finishedTo]);

  const activePartners = useMemo(() => {
    if (!Array.isArray(partnerList)) {
      return [];
    }

    if (!isOnlyActivePartnersShowed) {
      return partnerList;
    }

    return partnerList.filter(item => item.is_active);
  }, [partnerList, isOnlyActivePartnersShowed]);

  const handleDatesSelection = useCallback(
    dates => {
      const [start, end] = dates;

      setStartDate(start);
      setEndDate(end);

      if (end) {
        const endTime = end.setHours(23, 59, 59, 999);
        const endDate = new Date(endTime);
        setEndDate(endDate);
      }
    },
    [setStartDate, setEndDate]
  );

  const togglePartnersVisibility = useCallback(event => {
    setIsOnlyActivePartnersShowed(event.target.checked);
  }, []);

  function initStartDate() {
    const threeDaysAgo = new Date();

    threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
    threeDaysAgo.setHours(0, 0, 0, 0);

    return new Date(threeDaysAgo);
  }

  function initEndDate() {
    const today = new Date();

    today.setHours(23, 59, 59, 999);

    return new Date(today);
  }

  function getTimestamp(date) {
    return new Date(date).valueOf();
  }

  return (
    <Flex
      className="statistics-page"
      direction="column"
      pt={{ base: '150px', md: '75px' }}
    >
      <Header />

      <Box p="6px 0px 22px 0px">
        <Text fontSize="xl" color={textColor} fontWeight="bold">
          Статистика
        </Text>
      </Box>

      <Toolbar
        startedFrom={startedFrom}
        finishedTo={finishedTo}
        isOnlyActivePartnersShowed={isOnlyActivePartnersShowed}
        env={env}
        onDatesSelect={handleDatesSelection}
        onTogglePartnersVisibility={togglePartnersVisibility}
        onSelectEnv={onSelectEnv}
      />

      <BanksChart
        env={env}
        startedFrom={startedFrom}
        finishedTo={finishedTo}
        daysList={daysList}
        textColor={textColor}
        banks={bankList}
      />

      <ProvidersChart
        env={env}
        startedFrom={startedFrom}
        finishedTo={finishedTo}
        daysList={daysList}
        textColor={textColor}
      />

      <PartnersChart
        env={env}
        startedFrom={startedFrom}
        finishedTo={finishedTo}
        daysList={daysList}
        textColor={textColor}
        partners={activePartners}
      />

      <PartnersTable
        env={env}
        startedFrom={startedFrom}
        finishedTo={finishedTo}
        partners={activePartners}
      />
    </Flex>
  );
}
